// src/supabase/index.ts
import { jwtVerify } from "jose";
import { createClient } from "@supabase/supabase-js";
var SUPABASE_CONFIG = {
  url: "https://vxvmqkhdpwkxttmkuaxk.supabase.co",
  anonKey: "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZ4dm1xa2hkcHdreHR0bWt1YXhrIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MTMyODU1ODUsImV4cCI6MjAyODg2MTU4NX0.tCUQqnvOP8ZWJEHoBZkY5qmULC__CDbAjLNS-x3u-98"
};
var supabaseCookieOptions = {
  domain: process.env.NODE_ENV === "development" ? void 0 : "highlightai.com"
};
function createSupabase(superuserKey, options) {
  return createClient(
    "https://vxvmqkhdpwkxttmkuaxk.supabase.co",
    superuserKey ?? SUPABASE_CONFIG.anonKey,
    options
  );
}
async function verifySupabaseJWT(jwtSecret, jwt) {
  const verifiedJwt = await jwtVerify(jwt, new TextEncoder().encode(jwtSecret));
  return verifiedJwt.payload;
}
function extractJWTFromHeaders(headers) {
  const authorizationHeader = headers.get("Authorization");
  if (!authorizationHeader) {
    throw new Error("Authorization header is missing");
  }
  if (!authorizationHeader.startsWith("Bearer ")) {
    throw new Error("Authorization header must start with Bearer");
  }
  const jwt = authorizationHeader.substring(7);
  return jwt;
}
async function initUserMetadata(supabase, userId) {
  const selectResponse = await supabase.from("users_meta").select("*").eq("user_id", userId).maybeSingle();
  if (selectResponse.error) {
    throw new Error(selectResponse.error.message);
  }
  if (selectResponse.data) {
    return selectResponse.data;
  }
  const insertResponse = await supabase.from("users_meta").insert({ user_id: userId }).select("*").single();
  if (insertResponse.error) {
    throw new Error(insertResponse.error.message);
  }
  return insertResponse.data;
}

// src/builds/artifact-diff.ts
function normalizePathForComparison(path) {
  return path.split(/([/\\])/).map((segment, idx) => {
    if (idx % 2 === 1) return segment;
    return normalizeSegment(segment);
  }).join("");
}
function normalizeSegment(segment) {
  let s = segment;
  s = stripViteContentHash(s);
  s = stripSemverTags(s);
  s = s.replace(/[\s-]+\./g, ".").replace(/\s{2,}/g, " ").trim();
  return s;
}
function stripViteContentHash(segment) {
  let end = segment.length;
  let mapSuffix = "";
  if (end >= 4 && segment.slice(end - 4) === ".map") {
    mapSuffix = ".map";
    end -= 4;
  }
  let extStart = end;
  while (extStart > 0) {
    const ch = segment[extStart - 1];
    if (ch < "a" || ch > "z") {
      if (ch < "0" || ch > "9") break;
    }
    extStart--;
  }
  if (extStart >= end || segment[extStart - 1] !== ".") return segment;
  const beforeExt = extStart - 1;
  for (let hashLen = 12; hashLen >= 6; hashLen--) {
    const hashStart = beforeExt - hashLen;
    if (hashStart < 1 || segment[hashStart - 1] !== "-") continue;
    let hasUpperOrDigit = false;
    let ok = true;
    for (let i = hashStart; i < beforeExt; i++) {
      const ch = segment[i];
      const isUpper = ch >= "A" && ch <= "Z";
      const isLower = ch >= "a" && ch <= "z";
      const isDigit = ch >= "0" && ch <= "9";
      if (!isUpper && !isLower && !isDigit && ch !== "_" && ch !== "-") {
        ok = false;
        break;
      }
      if (isUpper || isDigit) hasUpperOrDigit = true;
    }
    if (!ok || !hasUpperOrDigit) continue;
    return segment.slice(0, hashStart - 1) + "." + segment.slice(extStart, end) + mapSuffix;
  }
  return segment;
}
function stripSemverTags(segment) {
  const parts = [];
  let i = 0;
  let keepStart = 0;
  while (i < segment.length) {
    const end = matchSemverAt(segment, i);
    if (end !== null) {
      if (i > keepStart) parts.push(segment.slice(keepStart, i));
      i = end;
      keepStart = end;
      continue;
    }
    i++;
  }
  if (keepStart === 0) return segment;
  if (keepStart < segment.length) parts.push(segment.slice(keepStart));
  return parts.join("");
}
function isWordChar(ch) {
  if (ch === void 0) return false;
  return ch >= "A" && ch <= "Z" || ch >= "a" && ch <= "z" || ch >= "0" && ch <= "9" || ch === "_";
}
function isIdentOrHyphen(ch) {
  return isWordChar(ch) || ch === "-";
}
function readDigits(segment, i) {
  let n = 0;
  while (i + n < segment.length && segment[i + n] >= "0" && segment[i + n] <= "9") n++;
  return n;
}
function isWordBoundaryAt(segment, pos) {
  return isWordChar(segment[pos - 1]) !== isWordChar(segment[pos]);
}
function matchSemverAt(segment, i) {
  if (isWordChar(segment[i - 1])) return null;
  let pos = i;
  for (let part = 0; part < 3; part++) {
    if (part > 0) {
      if (segment[pos] !== ".") return null;
      pos++;
    }
    const digits = readDigits(segment, pos);
    if (digits === 0) return null;
    pos += digits;
  }
  if (!isWordBoundaryAt(segment, pos)) return null;
  let best = pos;
  while (pos < segment.length && segment[pos] === "-") {
    const groupStart = pos;
    pos++;
    const identStart = pos;
    while (pos < segment.length && isIdentOrHyphen(segment[pos])) pos++;
    if (pos === identStart) {
      pos = groupStart;
      break;
    }
    while (pos > identStart && segment[pos - 1] === "-") pos--;
    if (pos === identStart) {
      pos = groupStart;
      break;
    }
    while (pos < segment.length && segment[pos] === ".") {
      const digits = readDigits(segment, pos + 1);
      if (digits === 0) break;
      pos += 1 + digits;
    }
    if (!isWordBoundaryAt(segment, pos)) {
      pos = groupStart;
      break;
    }
    best = pos;
  }
  return best;
}
function pairArtifactFiles(group, baselineGroup) {
  const currentByKey = /* @__PURE__ */ new Map();
  const baselineByKey = /* @__PURE__ */ new Map();
  for (const f of group?.files ?? []) {
    const key = normalizePathForComparison(f.path);
    const bucket = currentByKey.get(key);
    if (bucket) bucket.push(f);
    else currentByKey.set(key, [f]);
  }
  for (const f of baselineGroup?.files ?? []) {
    const key = normalizePathForComparison(f.path);
    const bucket = baselineByKey.get(key);
    if (bucket) bucket.push(f);
    else baselineByKey.set(key, [f]);
  }
  const out = [];
  const keys = /* @__PURE__ */ new Set([...currentByKey.keys(), ...baselineByKey.keys()]);
  for (const key of keys) {
    const currents = currentByKey.get(key) ?? [];
    const baselines = baselineByKey.get(key) ?? [];
    if (currents.length <= 1 && baselines.length <= 1) {
      const current = currents[0] ?? null;
      const baseline = baselines[0] ?? null;
      out.push({
        path: current?.path ?? baseline?.path ?? key,
        current,
        baseline,
        normalizedPath: key
      });
      continue;
    }
    out.push(...pairAmbiguousBucket(key, currents, baselines));
  }
  return out.sort(byImpact);
}
function pairAmbiguousBucket(key, currents, baselines) {
  const rows = [];
  const remainingCurrents = [...currents];
  const remainingBaselines = [...baselines];
  const baselineByPath = /* @__PURE__ */ new Map();
  for (const b of baselines) baselineByPath.set(b.path, b);
  for (let i = remainingCurrents.length - 1; i >= 0; i--) {
    const c = remainingCurrents[i];
    const exact = baselineByPath.get(c.path);
    if (!exact) continue;
    const baselineIdx = remainingBaselines.indexOf(exact);
    if (baselineIdx < 0) continue;
    remainingCurrents.splice(i, 1);
    remainingBaselines.splice(baselineIdx, 1);
    rows.push({ path: c.path, current: c, baseline: exact, normalizedPath: key });
  }
  const baselineByFingerprint = /* @__PURE__ */ new Map();
  for (const b of remainingBaselines) {
    if (b.chunk_fingerprint && !baselineByFingerprint.has(b.chunk_fingerprint)) {
      baselineByFingerprint.set(b.chunk_fingerprint, b);
    }
  }
  for (let i = remainingCurrents.length - 1; i >= 0; i--) {
    const c = remainingCurrents[i];
    const fp = c.chunk_fingerprint;
    if (!fp) continue;
    const match = baselineByFingerprint.get(fp);
    if (!match) continue;
    const baselineIdx = remainingBaselines.indexOf(match);
    if (baselineIdx < 0) continue;
    remainingCurrents.splice(i, 1);
    remainingBaselines.splice(baselineIdx, 1);
    baselineByFingerprint.delete(fp);
    rows.push({ path: c.path, current: c, baseline: match, normalizedPath: key });
  }
  remainingCurrents.sort((a, b) => b.size_bytes - a.size_bytes);
  for (const c of remainingCurrents) {
    if (remainingBaselines.length === 0) {
      rows.push({ path: c.path, current: c, baseline: null, normalizedPath: key });
      continue;
    }
    let bestIdx = 0;
    let bestDelta = Math.abs(c.size_bytes - remainingBaselines[0].size_bytes);
    for (let i = 1; i < remainingBaselines.length; i++) {
      const delta = Math.abs(c.size_bytes - remainingBaselines[i].size_bytes);
      if (delta < bestDelta) {
        bestDelta = delta;
        bestIdx = i;
      }
    }
    const match = remainingBaselines.splice(bestIdx, 1)[0];
    rows.push({ path: c.path, current: c, baseline: match, normalizedPath: key });
  }
  for (const b of remainingBaselines) {
    rows.push({ path: b.path, current: null, baseline: b, normalizedPath: key });
  }
  return rows;
}
function byImpact(a, b) {
  const aDelta = Math.abs((a.current?.size_bytes ?? 0) - (a.baseline?.size_bytes ?? 0));
  const bDelta = Math.abs((b.current?.size_bytes ?? 0) - (b.baseline?.size_bytes ?? 0));
  if (aDelta !== bDelta) return bDelta - aDelta;
  const aSize = a.current?.size_bytes ?? a.baseline?.size_bytes ?? 0;
  const bSize = b.current?.size_bytes ?? b.baseline?.size_bytes ?? 0;
  return bSize - aSize;
}
function classifyRow(row) {
  if (row.current && !row.baseline) return "added";
  if (!row.current && row.baseline) return "removed";
  if (row.current && row.baseline) {
    return row.current.size_bytes === row.baseline.size_bytes ? "unchanged" : "changed";
  }
  return "unchanged";
}
function countChanges(rows) {
  const counts = { added: 0, removed: 0, changed: 0, unchanged: 0, total: rows.length };
  for (const row of rows) {
    counts[classifyRow(row)]++;
  }
  return counts;
}
function parseFilterTokens(input) {
  return input.split(/[,\s]+/).map((token) => token.trim().toLowerCase()).filter((token) => token.length > 0);
}
function rowMatchesToken(row, token) {
  if (!token) return true;
  const current = row.current?.path.toLowerCase() ?? "";
  const baseline = row.baseline?.path.toLowerCase() ?? "";
  return current.includes(token) || baseline.includes(token);
}
function applyRowFilters(rows, searchTokens, excludeTokens) {
  if (searchTokens.length === 0 && excludeTokens.length === 0) return rows;
  return rows.filter((row) => {
    for (const token of searchTokens) {
      if (!rowMatchesToken(row, token)) return false;
    }
    for (const token of excludeTokens) {
      if (rowMatchesToken(row, token)) return false;
    }
    return true;
  });
}

// src/builds/channel-hash.ts
async function hashChannelAccessCode(code) {
  const msgBuffer = new TextEncoder().encode(code);
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgBuffer);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
}

// src/types/builds.ts
var BuildArch = /* @__PURE__ */ ((BuildArch2) => {
  BuildArch2["arm64"] = "arm64";
  BuildArch2["x64"] = "x64";
  return BuildArch2;
})(BuildArch || {});
var BuildPlatform = /* @__PURE__ */ ((BuildPlatform2) => {
  BuildPlatform2["windows"] = "win32";
  BuildPlatform2["macOS"] = "darwin";
  return BuildPlatform2;
})(BuildPlatform || {});
var StaticBuildChannel = /* @__PURE__ */ ((StaticBuildChannel2) => {
  StaticBuildChannel2["alpha"] = "alpha";
  StaticBuildChannel2["beta"] = "beta";
  StaticBuildChannel2["latest"] = "latest";
  StaticBuildChannel2["rc"] = "rc";
  StaticBuildChannel2["trustedTesters"] = "trusted-testers";
  return StaticBuildChannel2;
})(StaticBuildChannel || {});
var RELEASE_PROMOTION_DESTINATIONS = ["latest" /* latest */, "beta" /* beta */];
var RELEASE_PROMOTION_TARGETS = [
  { platform: "darwin" /* macOS */, arch: "arm64" /* arm64 */ },
  { platform: "darwin" /* macOS */, arch: "x64" /* x64 */ },
  { platform: "win32" /* windows */, arch: "arm64" /* arm64 */ },
  { platform: "win32" /* windows */, arch: "x64" /* x64 */ }
];
function isStaticChannel(channel) {
  return Object.values(StaticBuildChannel).includes(channel);
}
var BuildDistribution = /* @__PURE__ */ ((BuildDistribution2) => {
  BuildDistribution2["legacy"] = "legacy";
  BuildDistribution2["highlight"] = "highlight";
  return BuildDistribution2;
})(BuildDistribution || {});
var PRIMARY_DISTRIBUTION = "highlight" /* highlight */;
function isValidDistribution(value) {
  return typeof value === "string" && Object.values(BuildDistribution).includes(value);
}
function normalizeDistribution(distribution) {
  return isValidDistribution(distribution) ? distribution : "legacy" /* legacy */;
}
function isLegacyDistribution(distribution) {
  return normalizeDistribution(distribution) === "legacy" /* legacy */;
}
var CDN_PREFIX_BY_DISTRIBUTION = {
  ["legacy" /* legacy */]: "",
  ["highlight" /* highlight */]: "highlight"
};
function distributionCdnPrefix(distribution) {
  return CDN_PREFIX_BY_DISTRIBUTION[normalizeDistribution(distribution)];
}
function joinCdnPath(...segments) {
  return segments.map((segment) => typeof segment === "string" ? segment.replace(/^\/+|\/+$/g, "") : "").filter((segment) => segment.length > 0).join("/");
}
var CDN_ORIGIN = "https://cdn.highlightai.com";
function distributionCdnUrl(distribution, ...segments) {
  return `${CDN_ORIGIN}/${joinCdnPath(distributionCdnPrefix(distribution), ...segments)}`;
}
function buildCdnObjectPath(location, filename) {
  const { distribution, dynamic, channelValue, platform, arch } = location;
  return joinCdnPath(
    distributionCdnPrefix(distribution),
    dynamic ? `builds/${channelValue}` : "releases",
    platform,
    arch,
    filename
  );
}
function buildCdnObjectUrl(location, filename) {
  return `${CDN_ORIGIN}/${buildCdnObjectPath(location, filename)}`;
}
function manifestCdnObjectPath(location, suffix = ".yml") {
  const macSuffix = location.platform === "darwin" /* macOS */ ? "-mac" : "";
  return buildCdnObjectPath(location, `${location.channelValue}${macSuffix}${suffix}`);
}
function buildTargetKey(platform, arch, distribution) {
  return `${platform}-${arch}-${normalizeDistribution(distribution)}`;
}
function distributionKeySegment(distribution) {
  return isLegacyDistribution(distribution) ? [] : [normalizeDistribution(distribution)];
}

// src/builds/release-reporting.ts
var SEMVER_CORE_PATTERN = /^v?(\d+\.\d+\.\d+)(?:-[0-9A-Za-z.-]+)?(?:\+[0-9A-Za-z.-]+)?$/;
function releaseVersionCore(version) {
  return SEMVER_CORE_PATTERN.exec(version)?.[1] ?? version;
}
function versionWithChannel(version, channel) {
  return `${releaseVersionCore(version)}-${channel}`;
}
function installedVersionWithSuffix(version, installedChannel) {
  if (installedChannel === "latest" /* latest */ || installedChannel === "beta" /* beta */ || installedChannel === "rc" /* rc */) {
    return releaseVersionCore(version);
  }
  return versionWithChannel(version, installedChannel);
}
function sentryReleaseVersion(version, installedChannel) {
  if (installedChannel === "alpha" /* alpha */ || !isStaticChannel(installedChannel)) {
    return versionWithChannel(version, installedChannel);
  }
  return releaseVersionCore(version);
}
function reportingChannelAtStartup(startupChannel, installedChannel) {
  if (installedChannel === "latest" /* latest */ || installedChannel === "beta" /* beta */) {
    return startupChannel;
  }
  return installedChannel;
}
function sentryEnvironmentForChannel(channel) {
  return channel === "trusted-testers" /* trustedTesters */ ? "production" : channel;
}

// src/builds/release-selection.ts
var RELEASE_PROMOTION_BUILD_TARGETS = RELEASE_PROMOTION_TARGETS.map(
  (target) => ({
    ...target,
    distribution: "highlight" /* highlight */
  })
);
function compareBuildRecency(left, right) {
  return new Date(right.created_at).getTime() - new Date(left.created_at).getTime() || right.id - left.id;
}
function buildRunKey(build) {
  return `${build.semver}:${build.run_id ?? `build-${build.id}`}`;
}
function canonicalBuildsForRun(builds) {
  const canonicalByTarget = /* @__PURE__ */ new Map();
  for (const build of [...builds].sort(compareBuildRecency)) {
    const key = buildTargetKey(build.platform, build.arch, build.distribution);
    if (!canonicalByTarget.has(key)) {
      canonicalByTarget.set(key, build);
    }
  }
  return [...canonicalByTarget.values()];
}
function groupBuildRuns(builds) {
  const groups = /* @__PURE__ */ new Map();
  for (const build of builds) {
    const key = buildRunKey(build);
    groups.set(key, [...groups.get(key) ?? [], build]);
  }
  return [...groups.entries()].map(([key, group]) => {
    const newestFirst = [...group].sort(compareBuildRecency);
    return {
      key,
      semver: newestFirst[0].semver,
      runId: newestFirst[0].run_id,
      builds: newestFirst,
      canonicalBuilds: canonicalBuildsForRun(newestFirst),
      createdAt: newestFirst[0].created_at
    };
  }).sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime());
}
function buildMatchesReleaseTarget(build, target) {
  return build.platform === target.platform && build.arch === target.arch && normalizeDistribution(build.distribution) === target.distribution;
}
function isPublishableBuild(build) {
  return build.status === "success" && build.manifest?.version === build.semver;
}
function selectBuildRun(builds, expectedTargets) {
  const canonicalBuilds = canonicalBuildsForRun(builds);
  const canonicalByTarget = new Map(
    canonicalBuilds.map((build) => [buildTargetKey(build.platform, build.arch, build.distribution), build])
  );
  const buildFor = (target) => canonicalByTarget.get(buildTargetKey(target.platform, target.arch, target.distribution));
  const selectedBuilds = expectedTargets.flatMap((target) => {
    const build = buildFor(target);
    return build && isPublishableBuild(build) ? [build] : [];
  });
  const relevantBuilds = expectedTargets.flatMap((target) => {
    const build = buildFor(target);
    return build ? [build] : [];
  });
  const commitHashes = new Set(relevantBuilds.map((build) => build.commit_hash));
  return {
    canonicalBuilds,
    successfulBuilds: selectedBuilds,
    missingTargets: expectedTargets.filter((target) => {
      const build = buildFor(target);
      return !build || !isPublishableBuild(build);
    }),
    activeTargets: expectedTargets.filter((target) => buildFor(target)?.status === "active"),
    hasCommitConflict: commitHashes.has(null) || commitHashes.size > 1
  };
}

// src/product/uri.ts
import { z } from "zod";
var AudioNoteReferenceSchema = z.object({
  type: z.literal("audioNote"),
  id: z.string().min(1)
}).strict();
var StagedActionReferenceSchema = z.object({
  type: z.literal("stagedAction"),
  id: z.string().min(1)
}).strict();
var PRODUCT_REFERENCES = {
  audioNote: { route: "audio-notes", schema: AudioNoteReferenceSchema },
  stagedAction: { route: "staged-actions", schema: StagedActionReferenceSchema }
};
function highlightProductUri(reference) {
  const { route, schema } = PRODUCT_REFERENCES[reference.type];
  const parsed = schema.parse(reference);
  return `highlight://${route}/${encodeURIComponent(parsed.id)}`;
}
function parseHighlightProductUri(value) {
  let url;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "highlight:" && url.protocol !== "highlight-dev:" || url.username || url.password || url.port || url.search || url.hash) {
    return null;
  }
  const pathSegments = url.pathname.slice(1).split("/");
  const [route, ...idSegments] = url.hostname ? [url.hostname, ...pathSegments] : pathSegments;
  if (idSegments.length !== 1 || !idSegments[0]) {
    return null;
  }
  const matched = Object.entries(PRODUCT_REFERENCES).find(([, spec]) => spec.route === route);
  if (!matched) {
    return null;
  }
  const [type, { schema }] = matched;
  try {
    const parsed = schema.safeParse({ type, id: decodeURIComponent(idSegments[0]) });
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}
function audioNoteUri(reference) {
  return highlightProductUri(reference);
}
function parseAudioNoteUri(value) {
  const reference = parseHighlightProductUri(value);
  return reference?.type === "audioNote" ? reference : null;
}
function stagedActionUri(reference) {
  return highlightProductUri(reference);
}
function parseStagedActionUri(value) {
  const reference = parseHighlightProductUri(value);
  return reference?.type === "stagedAction" ? reference : null;
}

// src/product/attachments.ts
var ATTACHMENT_PREFIX = "attachment:";
var ATTACHMENT_REFERENCE = /\[[\s\S]*?\]\(attachment:(\d+)\)/g;
function isAttachmentUrl(url) {
  return typeof url === "string" && url.startsWith(ATTACHMENT_PREFIX);
}
function parseAttachmentIndex(url) {
  if (!isAttachmentUrl(url)) return null;
  const index = Number.parseInt(url.slice(ATTACHMENT_PREFIX.length), 10);
  return Number.isFinite(index) && index >= 0 ? index : null;
}
function missingAttachmentReferences(markdown, count) {
  const referenced = new Set(Array.from(markdown.matchAll(ATTACHMENT_REFERENCE), (match) => Number(match[1])));
  const missing = [];
  for (let index = 0; index < count; index++) {
    if (!referenced.has(index)) missing.push(`![Image ${index + 1}](${ATTACHMENT_PREFIX}${index})`);
  }
  return missing;
}

// src/product/attachment-limits.ts
var MAX_UPLOAD_FILE_BYTES = 50 * 1024 * 1024;
var MAX_UPLOAD_FILE_LABEL = "50 MB";
var MAX_INLINE_TEXT_BYTES = 19e5;
var MAX_INLINE_TEXT_LABEL = "1.9 MB";

// src/calendars/event-kind.ts
var NON_MEETING_EVENT_TYPES = /* @__PURE__ */ new Set([
  "workingLocation",
  "outOfOffice",
  "focusTime",
  "birthday",
  "fromGmail"
]);
function isNonMeetingEventType(eventType) {
  return typeof eventType === "string" && NON_MEETING_EVENT_TYPES.has(eventType);
}

// src/calendars/event-membership.ts
function isUserOnEvent(raw, isOwnCalendar, userEmails) {
  if (userEmails.size === 0) {
    return true;
  }
  const event = raw ?? {};
  const organizerEmail = typeof event.organizer?.email === "string" ? event.organizer.email.toLowerCase() : null;
  if (organizerEmail && userEmails.has(organizerEmail)) {
    return true;
  }
  const attendeeEmails = (Array.isArray(event.attendees) ? event.attendees : []).map((a) => a?.email).filter((email) => typeof email === "string").map((email) => email.toLowerCase());
  if (attendeeEmails.some((email) => userEmails.has(email))) {
    return true;
  }
  return attendeeEmails.length === 0 && isOwnCalendar;
}

// src/legal/index.ts
var LEGAL_SITE = "https://highlightai.com";
var LEGAL_DOCUMENT_PATHS = {
  terms: "/terms",
  privacy_policy: "/privacy-policy"
};
function legalDocumentUrl(type) {
  return `${LEGAL_SITE}${LEGAL_DOCUMENT_PATHS[type]}`;
}

// src/blog/index.ts
import { z as z2 } from "zod";
var BLOG_SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
var BLOG_URL = "https://highlightai.com/blog";
var BLOG_DEFAULT_AUTHOR = "Highlight Team";
var BLOG_DEFAULT_CATEGORY = "general";
function blogPostUrl(slug) {
  return slug ? `${BLOG_URL}/${slug}` : BLOG_URL;
}
function estimateBlogReadTime(body) {
  return Math.max(1, Math.ceil(body.split(/\s+/).filter(Boolean).length / 200));
}
function slugifyBlogTitle(title) {
  return title.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/[\s-]+/g, "-").replace(/^-+|-+$/g, "");
}
var optionalText = z2.string().nullable().transform((value) => value?.trim() || null);
var blogPostFieldsSchema = z2.object({
  published: z2.boolean(),
  slug: z2.string().regex(BLOG_SLUG_PATTERN, "must be lowercase letters, numbers, and single hyphens"),
  title: z2.string().trim().min(1, "must not be empty"),
  meta_title: optionalText,
  excerpt: z2.string(),
  body: z2.string(),
  author: z2.string().transform((value) => value.trim() || BLOG_DEFAULT_AUTHOR),
  author_image: optionalText,
  category: z2.string().transform((value) => value.trim().toLowerCase() || BLOG_DEFAULT_CATEGORY),
  tags: z2.array(z2.string()).transform((tags) => tags.map((tag) => tag.trim()).filter(Boolean)),
  featured: z2.boolean(),
  read_time: z2.number().finite().nullable().transform((minutes) => minutes === null ? null : Math.max(1, Math.round(minutes))),
  cover_image: optionalText
});
var blogPostUpdateSchema = blogPostFieldsSchema.partial();
var blogPostCreateSchema = blogPostUpdateSchema.required({ slug: true, title: true });
function toParseResult(result) {
  if (result.success) {
    return { input: result.data };
  }
  const issue = result.error.issues[0];
  if (!issue) {
    return { error: "Invalid blog post" };
  }
  return { error: issue.path.length > 0 ? `${issue.path.join(".")}: ${issue.message}` : issue.message };
}
function parseBlogPostCreate(raw) {
  return toParseResult(blogPostCreateSchema.safeParse(raw));
}
function parseBlogPostUpdate(raw) {
  return toParseResult(blogPostUpdateSchema.safeParse(raw));
}

// src/types/supabase.ts
var Constants = {
  public: {
    Enums: {}
  }
};

// ../billing-shared/src/index.ts
var BILLING_PLAN = {
  FREE: "free",
  LEGACY_PRO: "pro",
  LEGACY_PRO_TRIAL: "pro-trial",
  EAT: "eat",
  LITE: "lite",
  PRO2: "pro2",
  MAX: "max",
  LITE_TRIAL: "lite-trial",
  PRO2_TRIAL: "pro2-trial"
};
var HIGHLIGHT_BILLING_PLANS = [
  BILLING_PLAN.FREE,
  BILLING_PLAN.LEGACY_PRO,
  BILLING_PLAN.LEGACY_PRO_TRIAL,
  BILLING_PLAN.LITE,
  BILLING_PLAN.PRO2,
  BILLING_PLAN.MAX,
  BILLING_PLAN.LITE_TRIAL,
  BILLING_PLAN.PRO2_TRIAL
];
var V2_PLAN_TIERS = [BILLING_PLAN.LITE, BILLING_PLAN.PRO2, BILLING_PLAN.MAX];
var V2_TRIAL_PLAN_BY_TIER = {
  [BILLING_PLAN.LITE]: BILLING_PLAN.LITE_TRIAL,
  [BILLING_PLAN.PRO2]: BILLING_PLAN.PRO2_TRIAL
};
var V2_TRIAL_PLANS = [
  V2_TRIAL_PLAN_BY_TIER[BILLING_PLAN.LITE],
  V2_TRIAL_PLAN_BY_TIER[BILLING_PLAN.PRO2]
];
var V2_PLANS = [...V2_PLAN_TIERS, ...V2_TRIAL_PLANS];
var V2_TIER_BY_TRIAL_PLAN = Object.fromEntries(
  Object.entries(V2_TRIAL_PLAN_BY_TIER).map(([tier, trial]) => [trial, tier])
);
var isV2Plan = (plan) => plan != null && V2_PLANS.includes(plan);
var isV2TrialPlan = (plan) => plan != null && V2_TRIAL_PLANS.includes(plan);
var isV2PlanTier = (plan) => plan != null && V2_PLAN_TIERS.includes(plan);
var v2TierFor = (plan) => {
  if (!isV2Plan(plan)) return null;
  return isV2TrialPlan(plan) ? V2_TIER_BY_TRIAL_PLAN[plan] : plan;
};
var isTrialPlan = (plan) => plan === BILLING_PLAN.LEGACY_PRO_TRIAL || isV2TrialPlan(plan);
var planEndsAt = (profile) => profile.trialEndsAt ?? profile.cancelsAt ?? profile.currentPeriod.endingAt;
var V2_TRIAL_DAYS = 14;
var V2_TRIAL_ENDING_LEAD_DAYS = 3;
var DISCOUNT_GRANT_TERMS = {
  "early-tester-50": { percentOff: 50, durationMonths: 12, label: "Early tester discount" }
};
function discountedCents(listCents, percentOff) {
  return Math.round(listCents * (100 - percentOff) / 100);
}
function discountedUsd(listUsd, percentOff) {
  return discountedCents(Math.round(listUsd * 100), percentOff) / 100;
}
var LEGACY_PRO_PRICING = {
  label: "Pro",
  displayName: "Highlight Pro",
  monthlyPriceUsd: 20,
  checkoutDescription: "Premium AI Models, High quality meeting transcription, and more."
};
var V2_PLAN_PRICING = {
  [BILLING_PLAN.LITE]: {
    label: "Lite",
    displayName: "Highlight Lite",
    monthlyPriceUsd: 35,
    monthlyCredits: 500,
    checkoutDescription: "Meeting transcription, reminders, and chat."
  },
  [BILLING_PLAN.PRO2]: {
    label: "Pro",
    displayName: "Highlight Pro",
    monthlyPriceUsd: 60,
    monthlyCredits: 3e3,
    checkoutDescription: "Meeting transcription, reminders, skills, and chat."
  },
  [BILLING_PLAN.MAX]: {
    label: "Max",
    displayName: "Highlight Max",
    monthlyPriceUsd: 200,
    monthlyCredits: 8e3,
    checkoutDescription: "Meeting transcription, reminders, chat, custom skills, and white glove support."
  }
};
var planDisplayName = (plan) => {
  if (plan === BILLING_PLAN.FREE) return "Free";
  const tier = v2TierFor(plan);
  const base = tier ? V2_PLAN_PRICING[tier].displayName : LEGACY_PRO_PRICING.displayName;
  return isTrialPlan(plan) ? `${base} Trial` : base;
};
var paidPlanTierName = (plan) => {
  if (plan === BILLING_PLAN.LEGACY_PRO) return LEGACY_PRO_PRICING.label;
  return isV2PlanTier(plan) ? V2_PLAN_PRICING[plan].label : null;
};
var V2_TRIAL_MONTHLY_CREDITS_BY_PLAN = {
  [BILLING_PLAN.LITE_TRIAL]: V2_PLAN_PRICING[BILLING_PLAN.LITE].monthlyCredits,
  [BILLING_PLAN.PRO2_TRIAL]: V2_PLAN_PRICING[BILLING_PLAN.PRO2].monthlyCredits
};
var HL1_TRIAL_DAYS = 30;
var hl1TrialDays = (perkTier, tier) => perkTier === BILLING_PLAN.PRO2 || tier === BILLING_PLAN.LITE ? HL1_TRIAL_DAYS : V2_TRIAL_DAYS;
var hl1TrialDaysByTier = (perkTier) => ({
  [BILLING_PLAN.LITE]: hl1TrialDays(perkTier, BILLING_PLAN.LITE),
  [BILLING_PLAN.PRO2]: hl1TrialDays(perkTier, BILLING_PLAN.PRO2)
});
var HL1_PAID_SOURCES = [
  "orb-pro",
  "orb-pro2",
  "stripe-pro",
  "stripe-pro-monthly",
  "stripe-pro-yearly",
  "stripe-unlimited"
];
var HL1_PREVIOUS_MONTHLY_PRICE_USD = {
  "orb-pro": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "orb-pro2": V2_PLAN_PRICING[BILLING_PLAN.PRO2].monthlyPriceUsd,
  "stripe-pro": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "stripe-pro-monthly": LEGACY_PRO_PRICING.monthlyPriceUsd,
  "stripe-pro-yearly": null,
  "stripe-unlimited": null
};
var MEETING_HOURS_BY_PLAN = {
  [BILLING_PLAN.FREE]: 0,
  [BILLING_PLAN.LEGACY_PRO]: 30,
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: 30,
  [BILLING_PLAN.EAT]: null,
  [BILLING_PLAN.LITE]: 20,
  [BILLING_PLAN.PRO2]: 50,
  [BILLING_PLAN.MAX]: 80,
  [BILLING_PLAN.LITE_TRIAL]: 20,
  [BILLING_PLAN.PRO2_TRIAL]: 50
};
var AUDIO_MINUTES_METRIC_NAME = "Audio Note Minutes";
var getMeetingHoursForPlan = (plan, edition) => {
  const table = MEETING_HOURS_BY_EDITION[edition];
  return plan != null && plan in table ? table[plan] : table[BILLING_PLAN.FREE];
};
var MINUTE_TOP_UP_MINUTES_PER_USD = 60;
var MINUTE_TOP_UP_USD_PER_MINUTE = 1 / MINUTE_TOP_UP_MINUTES_PER_USD;
var MINUTE_TOP_UP_MIN_MINUTES = 60;
var MINUTE_TOP_UP_MAX_MINUTES = 6e3;
var SKILL_TIERS = ["free", "lite", "pro", "max"];
var PRICING_EDITIONS = ["v2", "v2-revamp"];
var pricingEditionFor = (freeTierPlanRevamp) => freeTierPlanRevamp ? "v2-revamp" : "v2";
var ALL_PLAN_FEATURES = {
  planner: true,
  skillRuns: true,
  skillTier: "max",
  createCustomSkills: true,
  screenshotIngest: true,
  monthlyMeetingBriefs: null,
  monthlyActionUnlocks: null,
  meetingSummaries: true,
  modelChoice: true
};
var V2_FREE_PLAN_FEATURES = {
  ...ALL_PLAN_FEATURES,
  meetingSummaries: false,
  modelChoice: false
};
var LITE_PLAN_FEATURES = {
  planner: true,
  skillRuns: true,
  skillTier: "lite",
  createCustomSkills: false,
  screenshotIngest: false,
  monthlyMeetingBriefs: null,
  monthlyActionUnlocks: null,
  meetingSummaries: true,
  modelChoice: true
};
var FREE_PLAN_FEATURES = {
  ...LITE_PLAN_FEATURES,
  skillTier: "free",
  monthlyMeetingBriefs: 10,
  monthlyActionUnlocks: 3,
  modelChoice: false
};
var PRO_PLAN_FEATURES = {
  ...ALL_PLAN_FEATURES,
  skillTier: "pro",
  createCustomSkills: false
};
var V2_FEATURES_BY_PLAN = {
  [BILLING_PLAN.FREE]: V2_FREE_PLAN_FEATURES,
  [BILLING_PLAN.LEGACY_PRO]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.EAT]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LITE]: LITE_PLAN_FEATURES,
  [BILLING_PLAN.PRO2]: PRO_PLAN_FEATURES,
  [BILLING_PLAN.MAX]: ALL_PLAN_FEATURES,
  [BILLING_PLAN.LITE_TRIAL]: LITE_PLAN_FEATURES,
  [BILLING_PLAN.PRO2_TRIAL]: PRO_PLAN_FEATURES
};
var PLAN_FEATURES_BY_EDITION = {
  v2: V2_FEATURES_BY_PLAN,
  "v2-revamp": {
    ...V2_FEATURES_BY_PLAN,
    [BILLING_PLAN.FREE]: FREE_PLAN_FEATURES,
    [BILLING_PLAN.PRO2]: ALL_PLAN_FEATURES,
    [BILLING_PLAN.PRO2_TRIAL]: ALL_PLAN_FEATURES
  }
};
var isSkillTierAllowed = (features, tier) => SKILL_TIERS.indexOf(tier) <= SKILL_TIERS.indexOf(features.skillTier);
var getPlanFeaturesForPlan = (plan, edition) => {
  const table = PLAN_FEATURES_BY_EDITION[edition];
  if (plan == null || !(plan in table)) return ALL_PLAN_FEATURES;
  return table[plan];
};
var cheapestPlanWith = (edition, has) => PLAN_LINEUP_BY_EDITION[edition].filter(isV2PlanTier).find((plan) => has(getPlanFeaturesForPlan(plan, edition))) ?? BILLING_PLAN.MAX;
var v2TermsFor = (tier) => ({
  label: V2_PLAN_PRICING[tier].label,
  monthlyPriceUsd: V2_PLAN_PRICING[tier].monthlyPriceUsd,
  monthlyCredits: V2_PLAN_PRICING[tier].monthlyCredits,
  monthlyMeetingHours: MEETING_HOURS_BY_PLAN[tier],
  monthlyMeetingBriefs: null
});
var V2_PLAN_TERMS = {
  [BILLING_PLAN.FREE]: {
    label: "Free",
    monthlyPriceUsd: 0,
    monthlyCredits: 0,
    monthlyMeetingHours: 0,
    monthlyMeetingBriefs: null
  },
  [BILLING_PLAN.LITE]: v2TermsFor(BILLING_PLAN.LITE),
  [BILLING_PLAN.PRO2]: v2TermsFor(BILLING_PLAN.PRO2),
  [BILLING_PLAN.MAX]: v2TermsFor(BILLING_PLAN.MAX)
};
var V2_REVAMP_PLAN_TERMS = {
  [BILLING_PLAN.FREE]: {
    label: "Free",
    monthlyPriceUsd: 0,
    monthlyCredits: 500,
    monthlyMeetingHours: 10,
    monthlyMeetingBriefs: FREE_PLAN_FEATURES.monthlyMeetingBriefs
  },
  [BILLING_PLAN.LITE]: V2_PLAN_TERMS[BILLING_PLAN.LITE],
  [BILLING_PLAN.PRO2]: {
    ...V2_PLAN_TERMS[BILLING_PLAN.PRO2],
    monthlyPriceUsd: 35,
    monthlyCredits: 8e3,
    monthlyMeetingHours: 200
  },
  [BILLING_PLAN.MAX]: { ...V2_PLAN_TERMS[BILLING_PLAN.MAX], monthlyCredits: 6e4, monthlyMeetingHours: 200 }
};
var PLAN_TERMS_BY_EDITION = {
  v2: V2_PLAN_TERMS,
  "v2-revamp": V2_REVAMP_PLAN_TERMS
};
var PLAN_LINEUP_BY_EDITION = {
  v2: V2_PLAN_TIERS,
  "v2-revamp": [BILLING_PLAN.FREE, BILLING_PLAN.PRO2, BILLING_PLAN.MAX]
};
var getPlanTerms = (plan, edition) => PLAN_TERMS_BY_EDITION[edition][plan];
var OFFERED_PLANS_BY_EDITION = {
  v2: HIGHLIGHT_BILLING_PLANS,
  "v2-revamp": [
    BILLING_PLAN.FREE,
    BILLING_PLAN.LEGACY_PRO_TRIAL,
    BILLING_PLAN.PRO2,
    BILLING_PLAN.MAX,
    BILLING_PLAN.PRO2_TRIAL
  ]
};
var ORB_MANAGED_PLANS_BY_EDITION = {
  v2: V2_PLANS,
  "v2-revamp": [...V2_PLANS, BILLING_PLAN.FREE]
};
var getOrbPlanTerms = (plan, edition) => {
  if (isV2TrialPlan(plan)) {
    const { monthlyCredits: monthlyCredits2, monthlyMeetingHours: monthlyMeetingHours2 } = getPlanTerms(V2_TIER_BY_TRIAL_PLAN[plan], edition);
    return { monthlyPriceUsd: null, monthlyCredits: monthlyCredits2, monthlyMeetingHours: monthlyMeetingHours2 };
  }
  const { monthlyPriceUsd, monthlyCredits, monthlyMeetingHours } = getPlanTerms(plan, edition);
  return { monthlyPriceUsd, monthlyCredits, monthlyMeetingHours };
};
var isMeteredPlan = (plan, edition) => plan != null && ORB_MANAGED_PLANS_BY_EDITION[edition].includes(plan);
var meetingHoursOn = (edition) => ({
  ...MEETING_HOURS_BY_PLAN,
  ...Object.fromEntries(
    ORB_MANAGED_PLANS_BY_EDITION[edition].map((plan) => [plan, getOrbPlanTerms(plan, edition).monthlyMeetingHours])
  )
});
var MEETING_HOURS_BY_EDITION = {
  v2: meetingHoursOn("v2"),
  "v2-revamp": meetingHoursOn("v2-revamp")
};
var INTERNAL_BILLING_PLANS = [...HIGHLIGHT_BILLING_PLANS, BILLING_PLAN.EAT];
var EXTERNAL_PLAN_ID_BY_PLAN = {
  [BILLING_PLAN.FREE]: "highlight-free",
  [BILLING_PLAN.LEGACY_PRO]: "highlight-pro",
  [BILLING_PLAN.LEGACY_PRO_TRIAL]: "highlight-pro-trial",
  [BILLING_PLAN.EAT]: "highlight-eat",
  [BILLING_PLAN.LITE]: "highlight-lite",
  [BILLING_PLAN.PRO2]: "highlight-pro2",
  [BILLING_PLAN.MAX]: "highlight-max",
  [BILLING_PLAN.LITE_TRIAL]: "highlight-lite-trial",
  [BILLING_PLAN.PRO2_TRIAL]: "highlight-pro2-trial"
};
var EXTERNAL_BILLING_PLAN_IDS = [
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.FREE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO_TRIAL],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.EAT],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.MAX],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE_TRIAL],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2_TRIAL]
];
var PAID_RECURRING_EXTERNAL_PLAN_IDS = [
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LEGACY_PRO],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.LITE],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.PRO2],
  EXTERNAL_PLAN_ID_BY_PLAN[BILLING_PLAN.MAX]
];
var PLAN_BY_EXTERNAL_PLAN_ID = Object.fromEntries(
  Object.entries(EXTERNAL_PLAN_ID_BY_PLAN).map(([plan, externalId]) => [externalId, plan])
);
var CREDITS_PER_USD = 100;
var CREDIT_TOP_UP_MIN_USD = 5;
var CREDIT_TOP_UP_MAX_USD = 500;

// src/types/subscription.ts
var BILLING_SIMULATION_MODES = ["off", "out-of-hours", "out-of-credits", "plan-ended"];
var BILLING_SIMULATION_STORE_KEY = "dev.billingSimulation";
var defaultPaywallState = {
  plan: void 0,
  isTrial: false,
  credits: { used: 0, total: 0, remaining: 0, percentage: 0 },
  meetingHours: { used: 0, total: 0, remaining: 0, percentage: 0 },
  atWarning: false,
  atLimit: false,
  nagBarsDismissed: {},
  nagBarsPermanentlyDismissed: false,
  missedMeetings: 0,
  missedHighlights: 0,
  isLoading: true,
  isLegacyBilling: false
};
var defaultSubscriptionInfo = {
  active: false,
  isLegacyBilling: false
};
var defaultUnifiedBillingState = {
  paywall: defaultPaywallState,
  subscription: defaultSubscriptionInfo,
  billingProfile: null,
  usage: null,
  lastUpdated: 0,
  creditConsumption: null
};
export {
  AUDIO_MINUTES_METRIC_NAME,
  AudioNoteReferenceSchema,
  BILLING_PLAN,
  BILLING_SIMULATION_MODES,
  BILLING_SIMULATION_STORE_KEY,
  BLOG_DEFAULT_AUTHOR,
  BLOG_DEFAULT_CATEGORY,
  BLOG_SLUG_PATTERN,
  BuildArch,
  BuildDistribution,
  BuildPlatform,
  CDN_ORIGIN,
  CREDITS_PER_USD,
  CREDIT_TOP_UP_MAX_USD,
  CREDIT_TOP_UP_MIN_USD,
  Constants,
  DISCOUNT_GRANT_TERMS,
  HL1_PAID_SOURCES,
  HL1_TRIAL_DAYS,
  INTERNAL_BILLING_PLANS,
  LEGACY_PRO_PRICING,
  MAX_INLINE_TEXT_BYTES,
  MAX_INLINE_TEXT_LABEL,
  MAX_UPLOAD_FILE_BYTES,
  MAX_UPLOAD_FILE_LABEL,
  MEETING_HOURS_BY_PLAN,
  MINUTE_TOP_UP_MAX_MINUTES,
  MINUTE_TOP_UP_MINUTES_PER_USD,
  MINUTE_TOP_UP_MIN_MINUTES,
  NON_MEETING_EVENT_TYPES,
  PLAN_LINEUP_BY_EDITION,
  PRICING_EDITIONS,
  PRIMARY_DISTRIBUTION,
  RELEASE_PROMOTION_BUILD_TARGETS,
  RELEASE_PROMOTION_DESTINATIONS,
  RELEASE_PROMOTION_TARGETS,
  SKILL_TIERS,
  SUPABASE_CONFIG,
  StagedActionReferenceSchema,
  StaticBuildChannel,
  V2_PLANS,
  V2_PLAN_PRICING,
  V2_TIER_BY_TRIAL_PLAN,
  V2_TRIAL_DAYS,
  V2_TRIAL_ENDING_LEAD_DAYS,
  V2_TRIAL_PLAN_BY_TIER,
  applyRowFilters,
  audioNoteUri,
  blogPostUrl,
  buildCdnObjectPath,
  buildCdnObjectUrl,
  buildMatchesReleaseTarget,
  buildTargetKey,
  canonicalBuildsForRun,
  cheapestPlanWith,
  classifyRow,
  countChanges,
  createSupabase,
  defaultPaywallState,
  defaultSubscriptionInfo,
  defaultUnifiedBillingState,
  discountedUsd,
  distributionCdnPrefix,
  distributionCdnUrl,
  distributionKeySegment,
  estimateBlogReadTime,
  extractJWTFromHeaders,
  getMeetingHoursForPlan,
  getPlanFeaturesForPlan,
  getPlanTerms,
  groupBuildRuns,
  hashChannelAccessCode,
  highlightProductUri,
  hl1TrialDays,
  hl1TrialDaysByTier,
  initUserMetadata,
  installedVersionWithSuffix,
  isAttachmentUrl,
  isLegacyDistribution,
  isMeteredPlan,
  isNonMeetingEventType,
  isPublishableBuild,
  isSkillTierAllowed,
  isStaticChannel,
  isTrialPlan,
  isUserOnEvent,
  isV2Plan,
  isV2PlanTier,
  isV2TrialPlan,
  isValidDistribution,
  joinCdnPath,
  legalDocumentUrl,
  manifestCdnObjectPath,
  missingAttachmentReferences,
  normalizeDistribution,
  normalizePathForComparison,
  paidPlanTierName,
  pairArtifactFiles,
  parseAttachmentIndex,
  parseAudioNoteUri,
  parseBlogPostCreate,
  parseBlogPostUpdate,
  parseFilterTokens,
  parseHighlightProductUri,
  parseStagedActionUri,
  planDisplayName,
  planEndsAt,
  pricingEditionFor,
  releaseVersionCore,
  reportingChannelAtStartup,
  rowMatchesToken,
  selectBuildRun,
  sentryEnvironmentForChannel,
  sentryReleaseVersion,
  slugifyBlogTitle,
  stagedActionUri,
  supabaseCookieOptions,
  v2TierFor,
  verifySupabaseJWT
};
//# sourceMappingURL=index.js.map